import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query';
import { showServerErrorToast } from 'src/components/toast/toast-helper';
import { FileToFileParameter } from 'src/helpers/file-helper';
import { useStudy } from 'src/helpers/hooks/useStudy';
import { IStudyAttachmentDto } from 'src/services/api/api-client';
import { addAttachmentToStudy } from 'src/services/api/api-client/StudyClient';
import { AppAttachmentValue } from 'uikit/appAttachmentsV2/AppAttachmentsV2.d';

export const useFileUploader = (onBefore?: () => void, onComplete?: (data: IStudyAttachmentDto[]) => void) => {
  const queryClient = useQueryClient();
  const { id: studyId } = useStudy() ?? {};
  const PROGRESS_KEY = ['upload-progress'];

  const mutation = useMutation({
    mutationKey: ['file-upload-task'],
    cacheTime: 1000 * 60 * 15,
    mutationFn: async (files: AppAttachmentValue[]) => {
      const fileParameters = files.map((file) => FileToFileParameter(file.file!));
      onBefore?.();
      queryClient.setQueryData(PROGRESS_KEY, {
        percents: 0,
        files: files.map((file) => ({ fileName: file.fileName, fileSize: file.fileSizeBytes })),
      });

      const result = await addAttachmentToStudy(studyId!, fileParameters, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.progress ?? 0) * 100);
          queryClient.setQueryData(PROGRESS_KEY, {
            percents: percentCompleted,
            files: files.map((file) => ({ fileName: file.fileName, fileSize: file.fileSizeBytes })),
          });
        },
      });

      return result;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(PROGRESS_KEY, { percents: 0, files: [] });
      if (onComplete) onComplete(data);
    },
    onError: (err) => {
      queryClient.setQueryData(PROGRESS_KEY, { percents: 0, files: [] });
      showServerErrorToast(err);
    },
  });

  const { data: progress } = useQuery(PROGRESS_KEY, {
    enabled: false,
    initialData: {
      percents: 0,
      files: [] as { fileName: string; fileSize: number }[],
    },
    staleTime: Infinity,
  });

  return {
    upload: mutation.mutate,
    isLoading: mutation.isLoading,
    progress,
  };
};
